'use client';
import React, { useEffect, useRef, useState } from 'react';

interface Slide {
  id: number;
  title: string;
  subtitle: string;
  description: string;
  emoji: string;
  bg: string;
  tag: string;
}

const slides: Slide[] = [
{
  id: 1,
  title: 'Custom Ecom Packaging',
  subtitle: 'For a D2C Fashion Brand',
  description: 'Branded kraft boxes, tissue paper, and thank you cards — a complete unboxing experience.',
  emoji: '📦',
  bg: 'bg-primary-dark',
  tag: 'E-Commerce'
},
{
  id: 2,
  title: 'No-Plastic Conference Kit',
  subtitle: 'For a Tech Summit',
  description: 'Kraft folders, seed paper notepads, cotton lanyards, and seed pens for 500+ delegates.',
  emoji: '🌱',
  bg: 'bg-[#1a3a2e]',
  tag: 'Events'
},
{
  id: 3,
  title: 'Cotton Bag Collection',
  subtitle: 'For a Retail Chain',
  description: 'Custom printed tote bags, saree bags, and drawstring bags in natural cotton.',
  emoji: '👜',
  bg: 'bg-[#2d4a1e]',
  tag: 'Apparel'
},
{
  id: 4,
  title: 'Glass Jar Packaging',
  subtitle: 'For an Artisan Food Brand',
  description: 'Premium glass jars with custom labels, honeycomb paper wrapping, and branded boxes.',
  emoji: '🫙',
  bg: 'bg-[#1e3a2e]',
  tag: 'Premium'
}];


export default function ShowcaseSlider() {
  const [current, setCurrent] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {if (entry.isIntersecting) setIsVisible(true);},
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isPlaying) {
      intervalRef.current = setInterval(() => {
        setCurrent((prev) => (prev + 1) % slides.length);
      }, 4000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPlaying]);

  const goTo = (index: number) => {
    setCurrent(index);
    setIsPlaying(false);
    setTimeout(() => setIsPlaying(true), 8000);
  };

  const slide = slides[current];

  return (
    <section ref={sectionRef} className="py-12 lg:py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className={`transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="text-center mb-8">
            <h2
              className="text-2xl md:text-3xl font-bold text-primary-dark"
              style={{ fontFamily: 'Fraunces, serif' }}>

              Custom projects done for our customers
            </h2>
            <p className="text-gray-400 font-normal mt-2"></p>
          </div>

          <div className="relative rounded-2xl overflow-hidden shadow-lg">
            {/* Slide */}
            <div
              key={slide.id}
              className={`${slide.bg} min-h-[320px] md:min-h-[400px] flex items-center animate-fade-in`}>

              <div className="max-w-7xl mx-auto px-8 py-12 grid grid-cols-1 md:grid-cols-2 gap-8 items-center w-full">
                <div className="text-white">
                  <span className="inline-block bg-white/20 text-white text-xs font-semibold px-3 py-1 rounded-full mb-4">
                    {slide.tag}
                  </span>
                  <h3
                    className="text-2xl md:text-3xl font-bold text-white mb-2"
                    style={{ fontFamily: 'Fraunces, serif' }}>

                    {slide.title}
                  </h3>
                  <p className="text-white/70 font-medium mb-3">{slide.subtitle}</p>
                  <p className="text-white/60 font-normal leading-relaxed">{slide.description}</p>
                  <a href="#contact" className="inline-flex items-center gap-2 bg-white text-primary-dark font-semibold px-6 py-2.5 rounded-btn hover:bg-gray-50 transition-all duration-200 mt-6 text-sm">
                    Get Similar Packaging
                  </a>
                </div>
                <div className="flex items-center justify-center">
                  <div className="bg-white/10 rounded-2xl p-12 backdrop-blur-sm">
                    <span className="text-8xl">{slide.emoji}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Controls */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
              {slides.map((_, i) =>
              <button
                key={i}
                onClick={() => goTo(i)}
                className={`h-2 rounded-full transition-all duration-200 ${
                i === current ? 'bg-white w-6' : 'bg-white/40 w-2.5 hover:bg-white/70'}`
                }
                aria-label={`Go to slide ${i + 1}`} />

              )}
            </div>

            {/* Prev/Next */}
            <button
              onClick={() => goTo((current - 1 + slides.length) % slides.length)}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white rounded-full p-2 transition-all duration-200 backdrop-blur-sm"
              aria-label="Previous slide">

              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={() => goTo((current + 1) % slides.length)}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white rounded-full p-2 transition-all duration-200 backdrop-blur-sm"
              aria-label="Next slide">

              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>);

}